// Order Accordion

import { useState } from "react";
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	Divider,
	FormControl,
	Select,
	MenuItem,
	Stack,
	List,
	ListItem,
	Chip,
	Box,
	Switch,
	FormControlLabel,
	Dialog,
	IconButton,
} from "custom_exports/material_ui_items";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PropTypes from "prop-types";
import {
	RequireUserRoleAsCustomer,
	RequireUserRoleAsNotCustomer,
} from "components/common/RequirePermission";
import { convertFirstCharacterToUpperCase } from "common/helpers";
import BackorderNotificationModal from "components/modals/BackorderNotificationModal";
import K from "common/keywords";
import C from "common/constants";

function OrderAccordionItem(props) {
	//--------------------------------- variables ---------------------------------//
	// ---- destructuring ---- //
	const { handleOnClick: rootHandleOnClick, order, isServiceRequestView = false, isBackorderedView = false } = props;

	// ---- state ---- //
	const [imageDialogOpen, setImageDialogOpen] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);

	// Backorder notification modal state
	const [backorderModalOpen, setBackorderModalOpen] = useState(false);
	const [pendingBackorderData, setPendingBackorderData] = useState(null);

	// Safe check: Ensure cart_items exists and is an array before checking
	const hasServiceItems =
		order?.cart_items && Array.isArray(order.cart_items)
			? order.cart_items.some(item => item?.product_details && !item.product_details.is_physical)
			: false;

	// Check if order has backordered items but order status is not backordered
	// This is used to show "Contains Backordered Items" chip in backordered view
	const hasBackorderedItems =
		order?.cart_items && Array.isArray(order.cart_items)
			? order.cart_items.some(item => item?.status === C.cartItemStatuses.backordered)
			: false;
	const isOrderFullyBackordered = order?.status === C.orderStatuses.backordered;
	const showBackorderedItemsChip = isBackorderedView && hasBackorderedItems && !isOrderFullyBackordered;


	// ---- assignments ---- //
	let orderStatusesList = [];
	let orderStatusesReverseDict = {};
	Object.keys(C.orderStatuses).forEach((key) => {
		if (!C.hiddenOrderStatuses.includes(key)) {
			orderStatusesList.push({ title: C.orderStatusLabels[key], id: C.orderStatuses[key] });
		}
		orderStatusesReverseDict[C.orderStatuses[key]] = C.orderStatusLabels[key];
	});

	let cartItemStatusesList = [];
	let cartItemStatusesReverseDict = {};
	Object.keys(C.cartItemStatuses).forEach((key) => {
		cartItemStatusesList.push({ title: key, id: C.cartItemStatuses[key] });
		cartItemStatusesReverseDict[C.cartItemStatuses[key]] = key;
	});

	// Safe status access with fallback
	const orderStatus = order?.status || C.orderStatuses.created;

	let statusBackgroundColor = "orange.light";
	if ([C.orderStatuses.completed].includes(orderStatus)) {
		statusBackgroundColor = "primary.light";
	} else if (
		[C.orderStatuses.cancelled, C.orderStatuses.rejected].includes(orderStatus)
	) {
		statusBackgroundColor = "red.light";
	} else if ([C.orderStatuses.backordered].includes(orderStatus)) {
		statusBackgroundColor = "#FFFF00";
	} else if ([C.orderStatuses.yorkton].includes(orderStatus)) {
		statusBackgroundColor = "#70bfff";
	}

	// Sanitize user-generated content to prevent XSS attacks
	// Uses browser's built-in text escaping without external dependencies
	const sanitizeText = (text) => {
		if (!text) return "";
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	};

	//--------------------------------- functions ---------------------------------//
	// ---- handlers ---- //
	const handleOnOrderStatusChange = (event, order) => {
		const {
			target: { value },
		} = event;

		// Validate order exists and has an id
		if (!order?.id) {
			console.error("handleOnOrderStatusChange: Invalid order data");
			return;
		}

		// If changing to backordered, show confirmation modal
		if (value === C.orderStatuses.backordered) {
			setPendingBackorderData({
				type: "order",
				orderId: order.id,
				newStatusId: value,
			});
			setBackorderModalOpen(true);
		} else {
			rootHandleOnClick(K.UOrderStatus, {
				orderId: order.id,
				newStatusId: value,
			});
		}
	};

	const handleOnCartItemStatusChange = (event, cartItem) => {
		const isBackordered = event.target.checked;
		const newStatusId = isBackordered ? C.cartItemStatuses.backordered : C.cartItemStatuses.active;

		// Validate cartItem exists and has an id
		if (!cartItem?.id) {
			console.error("handleOnCartItemStatusChange: Invalid cart item data");
			return;
		}

		// If marking as backordered, show confirmation modal
		if (isBackordered) {
			setPendingBackorderData({
				type: "item",
				cartItemId: cartItem.id,
				newStatusId: newStatusId,
			});
			setBackorderModalOpen(true);
		} else {
			rootHandleOnClick(K.UCartItemStatus, {
				cartItemId: cartItem.id,
				newStatusId: newStatusId,
			});
		}
	};

	const handleBackorderConfirm = (notifyCustomer) => {
		// Validate pendingBackorderData exists before proceeding
		if (!pendingBackorderData) {
			console.error("handleBackorderConfirm: No pending backorder data");
			setBackorderModalOpen(false);
			return;
		}

		// Ensure notifyCustomer is a boolean
		const shouldNotify = Boolean(notifyCustomer);

		if (pendingBackorderData.type === "order" && pendingBackorderData.orderId) {
			rootHandleOnClick(K.UOrderStatus, {
				orderId: pendingBackorderData.orderId,
				newStatusId: pendingBackorderData.newStatusId,
				notifyCustomer: shouldNotify,
			});
		} else if (pendingBackorderData.type === "item" && pendingBackorderData.cartItemId) {
			rootHandleOnClick(K.UCartItemStatus, {
				cartItemId: pendingBackorderData.cartItemId,
				newStatusId: pendingBackorderData.newStatusId,
				notifyCustomer: shouldNotify,
			});
		} else {
			console.error("handleBackorderConfirm: Invalid pending backorder data type or missing id");
		}

		setBackorderModalOpen(false);
		setPendingBackorderData(null);
	};

	const handleBackorderModalClose = () => {
		setBackorderModalOpen(false);
		setPendingBackorderData(null);
	};

	const handleImageClick = (image) => {
		setSelectedImage(image);
		setImageDialogOpen(true);
	};

	const handleImageDialogClose = () => {
		setImageDialogOpen(false);
		setSelectedImage(null);
	};

	//--------------------------------- contents ----------------------------------//
	const content = (
		<Accordion
			sx={{
				borderRadius: "16px !important",
				boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
				mb: 2,
				bgcolor: "white",
				"&:before": { display: "none" },
				overflow: "hidden",
				transition: "box-shadow 0.2s ease",
				"&:hover": {
					boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
				},
			}}
		>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				sx={{
					"& .MuiAccordionSummary-content": {
						flexDirection: { xs: "column", sm: "row" },
						justifyContent: "space-between",
						alignItems: { xs: "flex-start", sm: "center" },
						gap: { xs: 1, sm: 0 },
					},
				}}
			>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={{ xs: 0.5, sm: 0 }}
					sx={{ flex: 1, minWidth: 0, alignItems: { sm: "center" } }}
				>
					<RequireUserRoleAsNotCustomer>
						<Box
							sx={{
								width: { xs: "auto", sm: 220 },
								flexShrink: 0,
								mr: { xs: 0, sm: 0 },
								overflow: "hidden",
							}}
						>
							<Typography noWrap>
								{sanitizeText(order.placed_by_user_details?.profile?.business_name)}
							</Typography>
							<Typography variant="caption" noWrap sx={{ color: "grey.main", display: "block" }}>
								{sanitizeText(order.placed_by_user_details?.profile?.business_email)}
							</Typography>
						</Box>

						<Divider orientation="vertical" flexItem sx={{ ml: 2, display: { xs: "none", sm: "block" } }} />
					</RequireUserRoleAsNotCustomer>
					<Box sx={{ width: { xs: "auto", sm: 220 }, flexShrink: 0, pl: { xs: 0, sm: 2 } }}>
						<Typography noWrap sx={{ color: "grey.main", fontSize: { xs: "0.85rem", sm: "1rem" } }}>
							{order?.created_on
						? new Date(Date.parse(order.created_on)).toLocaleString()
						: "N/A"}
						</Typography>
					</Box>

					{hasServiceItems && !isServiceRequestView && (
						<>
							<Divider orientation="vertical" flexItem sx={{ ml: { xs: 0, sm: 2 }, display: { xs: "none", sm: "block" } }} />

							<Box sx={{ px: { xs: 0, sm: 2 } }}>
								<Chip
									label="Service Requests"
									size="small"
									sx={{
										bgcolor: "#e3f2fd",
										color: "#1565c0",
										fontWeight: "600",
										border: "1px solid #2196f3",
										"& .MuiChip-label": {
											fontSize: "0.7rem",
										},
										borderRadius: "16px",
										height: 24,
									}}
								/>
							</Box>
						</>
					)}

					{showBackorderedItemsChip && (
						<>
							<Divider orientation="vertical" flexItem sx={{ ml: { xs: 0, sm: 2 }, display: { xs: "none", sm: "block" } }} />

							<Box sx={{ px: { xs: 0, sm: 2 } }}>
								<Chip
									label="Backordered Items"
									size="small"
									sx={{
										bgcolor: "#FFFDE7",
										color: "#F57F17",
										fontWeight: "600",
										border: "1px solid #FFEE58",
										"& .MuiChip-label": {
											fontSize: "0.7rem",
										},
										borderRadius: "16px",
										height: 24,
									}}
								/>
							</Box>
						</>
					)}

				</Stack>
				<RequireUserRoleAsNotCustomer>
					<FormControl
						size="small"
						sx={{
							mr: { xs: 0, sm: 5 },
							mt: { xs: 1, sm: 0 },
							width: { xs: "100%", sm: 200 },
							maxWidth: 200,
							flexShrink: 0,
						}}
						onClick={(event) => event.stopPropagation()}
					>
						{/* <InputLabel>update status</InputLabel> */}
						<Select
							value={orderStatus}
							sx={{
								bgcolor: statusBackgroundColor,
								borderRadius: "16px",
								fontWeight: 600,
								fontSize: { xs: "0.85rem", sm: "1rem" },
							}}
							onClick={(event) => event.stopPropagation()}
							onChange={(event) =>
								handleOnOrderStatusChange(event, order)
							}
						>
							{orderStatusesList.map((status) => (
								<MenuItem key={status.id} value={status.id}>
									{convertFirstCharacterToUpperCase(status.title)}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</RequireUserRoleAsNotCustomer>
				<RequireUserRoleAsCustomer>
					<Chip
						label={convertFirstCharacterToUpperCase(
							orderStatusesReverseDict[orderStatus] || "Unknown"
						)}
						sx={{
							mr: { xs: 0, sm: 5 },
							mt: { xs: 1, sm: 0 },
							width: { xs: "auto", sm: 180 },
							minWidth: 140,
							bgcolor: statusBackgroundColor,
							borderRadius: "16px",
							fontWeight: 600,
							fontSize: { xs: "0.85rem", sm: "1rem" },
							flexShrink: 0,
						}}
					/>
				</RequireUserRoleAsCustomer>
			</AccordionSummary>
			<AccordionDetails sx={{ bgcolor: "white", pt: 0 }}>
				<List>
					{/* Safe check: Ensure cart_items exists and is an array */}
					{(order?.cart_items && Array.isArray(order.cart_items) ? order.cart_items : [])
						.filter(item => {
							// In service request view, show only non-physical items
							if (isServiceRequestView) {
								return item?.product_details && !item.product_details.is_physical;
							}
							// In normal orders view, show all items
							return true;
						})
						.map((item) => {
						const itemStatus = item.status || C.cartItemStatuses.active;
						const isBackordered = itemStatus === C.cartItemStatuses.backordered;

						return (
							<ListItem
								key={item.id}
								sx={{
									borderLeft: isBackordered
										? "4px solid #fbbf24"
										: "4px solid transparent",
									bgcolor: "transparent",
									flexDirection: "column",
									alignItems: "flex-start",
									py: 1.5,
									px: 2,
									borderBottom: "1px solid #f1f5f9",
									"&:last-child": {
										borderBottom: "none",
									},
								}}
							>
								{/* Row 1: Product title, quantity badge, service chip, and backordered toggle */}
								<Box sx={{
									display: "flex",
									flexDirection: { xs: "column", sm: "row" },
									alignItems: { xs: "flex-start", sm: "center" },
									width: "100%",
									gap: { xs: 0.5, sm: 1 }
								}}>
									{/* Product info row */}
									<Box sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										flexWrap: "wrap",
										flex: 1,
										minWidth: 0,
									}}>
										{/* Product Title */}
										<Typography variant="body2" sx={{ fontWeight: 500 }}>
											{sanitizeText(item.product_details?.title)}
										</Typography>

										{/* Quantity Badge */}
										<Chip
											label={`x${item.quantity || 1}`}
											size="small"
											color="primary"
											sx={{
												height: 20,
												fontSize: "0.75rem",
												fontWeight: 600,
												borderRadius: "10px",
											}}
										/>

										{/* Service Request Chip */}
										{!item.product_details?.is_physical && (
											<Chip
												label="Service"
												size="small"
												sx={{
													bgcolor: "#1565c0",
													color: "#ffffff",
													fontWeight: "600",
													height: 20,
													borderRadius: "10px",
													"& .MuiChip-label": {
														fontSize: "0.65rem",
														px: 1,
													},
												}}
											/>
										)}
									</Box>

									{/* Backordered Toggle - on its own row on mobile */}
									<Box sx={{
										display: "flex",
										alignItems: "center",
										flexShrink: 0,
									}}>
										<RequireUserRoleAsNotCustomer>
											<FormControlLabel
												control={
													<Switch
														checked={isBackordered}
														onChange={(event) => handleOnCartItemStatusChange(event, item)}
														onClick={(event) => event.stopPropagation()}
														color="warning"
														size="small"
													/>
												}
												label="Backordered"
												labelPlacement="start"
												sx={{
													m: 0,
													"& .MuiTypography-root": {
														fontSize: { xs: "0.75rem", sm: "0.8rem" }
													}
												}}
											/>
										</RequireUserRoleAsNotCustomer>
										<RequireUserRoleAsCustomer>
											{isBackordered && (
												<Chip
													label="Backordered"
													size="small"
													sx={{
														bgcolor: "#FFFF00",
														color: "#000000",
														fontWeight: "600",
														borderRadius: "10px",
													}}
												/>
											)}
										</RequireUserRoleAsCustomer>
									</Box>
								</Box>

								{/* Row 2: Description/Suggestion */}
								{item.product_suggestion && (
									<Typography
										variant="caption"
										sx={{ color: "grey.600", mt: 0.5 }}
									>
										{sanitizeText(item.product_suggestion)}
									</Typography>
								)}

								{/* Row 3: Service Images Thumbnails */}
								{item.service_images && item.service_images.length > 0 && (
									<Box
										sx={{
											display: "flex",
											flexWrap: "wrap",
											gap: 1,
											mt: 1,
											alignItems: "center",
										}}
									>
										<PhotoLibraryIcon
											sx={{ fontSize: 16, color: "grey.500" }}
										/>
										{item.service_images.map((image) => (
											<Box
												key={image.id}
												onClick={() => handleImageClick(image)}
												sx={{
													width: 45,
													height: 45,
													borderRadius: "8px",
													overflow: "hidden",
													border: "1px solid #e2e8f0",
													cursor: "pointer",
													transition: "transform 0.2s, box-shadow 0.2s",
													"&:hover": {
														transform: "scale(1.05)",
														boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
													},
												}}
											>
												<img
													src={image.url}
													alt={image.original_filename || "Service image"}
													onError={(e) => {
														e.target.style.display = 'none';
													}}
													style={{
														width: "100%",
														height: "100%",
														objectFit: "cover",
													}}
												/>
											</Box>
										))}
									</Box>
								)}
							</ListItem>
						);
					})}
				</List>
				{order?.suggestion && (
					<Box>
						<Typography
							variant="caption"
							sx={{ fontWeight: "500", color: "grey" }}
						>
							Order Suggestion
						</Typography>
						<Divider />
						<Typography variant="caption" sx={{ color: "black" }}>
							{sanitizeText(order.suggestion)}
						</Typography>
					</Box>
				)}
			</AccordionDetails>

			{/* Image Preview Dialog */}
			<Dialog
				open={imageDialogOpen}
				onClose={handleImageDialogClose}
				maxWidth="md"
				fullWidth
			>
				<Box sx={{ position: "relative" }}>
					<IconButton
						onClick={handleImageDialogClose}
						sx={{
							position: "absolute",
							top: 8,
							right: 8,
							bgcolor: "rgba(0,0,0,0.5)",
							color: "white",
							zIndex: 1,
							"&:hover": {
								bgcolor: "rgba(0,0,0,0.7)",
							},
						}}
					>
						<CloseIcon />
					</IconButton>
					{selectedImage && (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								p: 2,
							}}
						>
							<img
								src={selectedImage.url}
								alt={selectedImage.original_filename || "Service image"}
								style={{
									maxWidth: "100%",
									maxHeight: "70vh",
									objectFit: "contain",
								}}
							/>
							<Typography
								variant="caption"
								sx={{ mt: 1, color: "grey.600" }}
							>
								{selectedImage.original_filename}
								{selectedImage.file_size && (
									<> ({(selectedImage.file_size / 1024).toFixed(1)} KB)</>
								)}
							</Typography>
						</Box>
					)}
				</Box>
			</Dialog>

			{/* Backorder Notification Modal */}
			<BackorderNotificationModal
				open={backorderModalOpen}
				onClose={handleBackorderModalClose}
				onConfirm={handleBackorderConfirm}
				itemType={pendingBackorderData?.type || "order"}
			/>
		</Accordion>
	);

	return content;
}

OrderAccordionItem.propTypes = {
	handleOnClick: PropTypes.func.isRequired,
	order: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		status: PropTypes.number,
		created_on: PropTypes.string,
		suggestion: PropTypes.string,
		placed_by_user_details: PropTypes.shape({
			profile: PropTypes.shape({
				business_name: PropTypes.string,
				business_email: PropTypes.string,
			}),
		}),
		cart_items: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
				quantity: PropTypes.number,
				status: PropTypes.number,
				product_suggestion: PropTypes.string,
				product_details: PropTypes.shape({
					title: PropTypes.string,
					is_physical: PropTypes.bool,
				}),
				service_images: PropTypes.arrayOf(
					PropTypes.shape({
						id: PropTypes.string,
						url: PropTypes.string,
						original_filename: PropTypes.string,
						content_type: PropTypes.string,
						file_size: PropTypes.number,
					})
				),
			})
		),
	}).isRequired,
	isServiceRequestView: PropTypes.bool,
	isBackorderedView: PropTypes.bool,
};

export default OrderAccordionItem;
